<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import { useToast } from 'vue-toastification/dist/index.mjs'
import { getStatusColor } from '@/utils/statusUtils'

// Import team helpers for consistent styling
import { useAbility } from '@/plugins/casl/composables/useAbility'

// Import customer components
import CustomerSelector from '@/components/shipment/parcel/CustomerSelector.vue'
import ContactForm from '@/components/ContactForm.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  parcel: {
    type: Object,
    required: false,
    default: () => ({
      id: null,
      barcode: '',
      bag_code: '',
      date: '',
      status: '',
      status_key: '',
      dimension: '',
      volume: '',
      weight: '',
      transport: null,
      belongs_to: null,
      customer: {
        display: '',
        phone: '',
      },
      images: [],
      shipment_product_lines: [],
      commission_agent: null,
      notes: '',
    }),
  },
  canDelete: {
    type: Boolean,
    default: false,
  },
  canEdit: {
    type: Boolean,
    default: false,
  },
  editable: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'update:parcel', 'request-upload-images', 'parcel-updated'])

const { t, locale } = useI18n()
const router = useRouter()
const toast = useToast()

// Get team helpers for consistent styling
const { getTeamBadgeColor, getTeamDisplayName, isAdmin } = useAbility()

const dialog = ref(false)
const activeTab = ref(0)
const translatedProducts = ref('')
const activeImageIndex = ref(0)
const showImageViewer = ref(false)

// Add refs for editing
const isEditing = ref(false)
const isVerified = ref(false)
const editedParcel = ref(null)
const loading = ref(false)
const customerSelection = ref(null) // For storing the selected customer object

// Customer creation modal states
const showContactModal = ref(false)
const newCustomerName = ref('')

// Transport options
const transportOptions = [
  { value: 0, title: t('shipment.by_plane') },
  { value: 1, title: t('shipment.by_ship') },
]

// Belongs to options with full team names
const belongsToOptions = [
  { value: 0, title: getTeamDisplayName('GB') },
  { value: 1, title: getTeamDisplayName('GD') },
  { value: 2, title: getTeamDisplayName('JP') },
]

// Parcel type options
const parcelTypeOptions = [
  { value: 'standard', title: t('shipment.parcel_types.standard') },
  { value: 'battery', title: t('shipment.parcel_types.battery') },
  { value: 'fake_brand', title: t('shipment.parcel_types.fake_brand') },
  { value: 'fragile', title: t('shipment.parcel_types.fragile') },
  { value: 'liquid', title: t('shipment.parcel_types.liquid') },
  { value: 'electronics', title: t('shipment.parcel_types.electronics') },
  { value: 'clothing', title: t('shipment.parcel_types.clothing') },
  { value: 'food', title: t('shipment.parcel_types.food') },
  { value: 'other', title: t('shipment.parcel_types.other') },
]

// Computed properties
const dimension = computed(() => {
  if (!props.parcel?.dimension) return '-'
  
  return `${props.parcel.dimension} cm`
})

const volume = computed(() => {
  if (!props.parcel?.volume) return '-'
  
  return `${Number(props.parcel.volume).toFixed(3)} m³`
})

const weight = computed(() => {
  if (!props.parcel?.weight) return '-'
  
  return `${props.parcel.weight} kg`
})

const transportLabel = computed(() => {
  const labels = {
    0: t('shipment.by_plane'),
    1: t('shipment.by_ship'),
  }
  
  return labels[props.parcel?.transport] || '-'
})

const belongsToLabel = computed(() => {
  // Use the new team structure from the API
  if (props.parcel?.team_display) {
    return props.parcel.team_display
  }
  if (props.parcel?.team?.display_name) {
    return props.parcel.team.display_name
  }
  if (props.parcel?.team?.name) {
    return props.parcel.team.name
  }
  if (props.parcel?.team_code) {
    return getTeamDisplayName(props.parcel.team_code)
  }
  return '-'
})

// Get team color for belongs_to
const getBelongsToColor = computed(() => {
  // Use the team color from the API if available
  if (props.parcel?.team?.color) {
    return props.parcel.team.color
  }
  // Fall back to team code mapping
  if (props.parcel?.team_code) {
    return getTeamBadgeColor(props.parcel.team_code)
  }
  if (props.parcel?.team?.code) {
    return getTeamBadgeColor(props.parcel.team.code)
  }
  return 'secondary'
})

// Get parcel type color
const getParcelTypeColor = parcelType => {
  switch (parcelType) {
  case 'battery':
    return 'orange'
  case 'fake_brand':
    return 'red'
  case 'fragile':
    return 'amber'
  case 'liquid':
    return 'blue'
  case 'electronics':
    return 'purple'
  case 'clothing':
    return 'green'
  case 'food':
    return 'teal'
  case 'other':
    return 'grey'
  case 'standard':
  default:
    return 'primary'
  }
}

// Methods
const openImageViewer = index => {
  activeImageIndex.value = index
  showImageViewer.value = true
}

watch(() => props.modelValue, val => {
  dialog.value = val
})

watch(() => props.parcel, newParcel => {
  if (newParcel) {
    // Create a safer version of the parcel data with defaults for missing values
    editedParcel.value = {
      ...newParcel,
      id: newParcel.id || null,
      barcode: newParcel.barcode || '',
      status_key: newParcel.status_key || newParcel.status || 'pending_verification',
      transport: Number(newParcel.transport || 0),
      belongs_to: Number(newParcel.belongs_to || 0),
      customer_id: newParcel.customer?.id || null,
      customer: newParcel.customer || null,
      weight: Number(newParcel.weight || 0),
      length: Number(newParcel.length || 0),
      width: Number(newParcel.width || 0),
      height: Number(newParcel.height || 0),
      parcel_type: newParcel.parcel_type || 'standard',
      notes: newParcel.notes || '',
    }
    
    // If we have customer info, set up the customer selection for CustomerSelector
    if (newParcel.customer) {
      // Create a customer object formatted for CustomerSelector
      const customerObj = {
        id: newParcel.customer.id,
        text: newParcel.customer.display || newParcel.customer.name || 'Unknown',
        name: newParcel.customer.name || 'Unknown',
        phone: newParcel.customer.phone || '',
      }
      
      // Set as the selected customer in CustomerSelector
      customerSelection.value = customerObj
    } else {
      customerSelection.value = null
    }
    
    isVerified.value = newParcel.status_key === 'received'
  }
}, { deep: true })

watch(dialog, val => {
  emit('update:modelValue', val)
  
  // Fetch translated products when dialog opens
  if (val && props.parcel && props.parcel.id) {
    // Ensure we refresh the product data with current locale
    fetchTranslatedProducts()
  }
})

// Watch for locale changes to update translations
watch(locale, newLocale => {
  if (props.parcel && props.parcel.id) {
    fetchTranslatedProducts()
  }
}, { immediate: true }) // Immediately run when component is mounted

// Function to fetch updated products in current locale
// Function to map frontend locale to backend locale
const mapLocale = frontendLocale => {
  // Map frontend locale codes to backend locale codes
  const localeMap = {
    'zh': 'ce', // Chinese in frontend is 'zh', but backend uses 'ce'
    'cn': 'ce', // Alternative Chinese code
    'fr': 'fr', // French is same in both
    'en': 'en',  // English is same in both
  }
  
  // Return the mapped locale or the original if no mapping exists
  return localeMap[frontendLocale] || frontendLocale
}

// Function to get the product name in the current locale from a translations object
// The product.name structure will be like: { "en": "Water", "fr": "Eau", "ce": "水" }
const getLocalizedProductName = product => {
  if (!product) return '-'
  
  // If the product name is a string, return it directly
  if (typeof product.name === 'string') return product.name
  
  // If the product has name translations as an object
  if (product.name && typeof product.name === 'object') {
    const backendLocale = mapLocale(locale.value)
    
    // Try to get the name in the current locale
    if (product.name[backendLocale]) {
      return product.name[backendLocale]
    }
    
    // Fall back to English if available
    if (product.name.en) {
      return product.name.en
    }
    
    // Fall back to Chinese if available
    if (product.name.ce) {
      return product.name.ce
    }
    
    // Fall back to the first available translation
    const firstKey = Object.keys(product.name)[0]
    if (firstKey) {
      return product.name[firstKey]
    }
  }
  
  // If we can't find a translation, return '-'
  return '-'
}

const retryFetchTranslatedParcel = async (id, maxAttempts = 3, delay = 500) => {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await axios.get(`/shipment/incoming-parcels/${id}?include_translations=true`)
    } catch (error) {
      if (attempt === maxAttempts) throw error
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
}

const fetchTranslatedProducts = async () => {
  try {
    // Request full translations from the API
    
    const response = await retryFetchTranslatedParcel(props.parcel.id)
    
    // Debug log to check response structure
    
    if (response.data) {
      const parcelData = response.data.data || response.data
      
      // Build translated products summary manually based on current locale
      if (parcelData.products && parcelData.products.length) {
        translatedProducts.value = parcelData.products
          .map(p => `${getLocalizedProductName(p)} (${p.quantity})`)
          .join(', ')
      } else {
        translatedProducts.value = parcelData.products_display_text || ''
      }
      
      // Update the parcel object with the latest data, including products with full translation objects
      emit('update:parcel', {
        ...props.parcel,
        products: parcelData.products || [],
        products_display_text: translatedProducts.value,
        products_display_details: parcelData.products_display_details || [],
      })
      
    }
  } catch (error) {
    console.error('Error fetching products data:', error)
  }
}

// Start editing mode
const startEditing = () => {
  isEditing.value = true
}

// Cancel editing and reset form
const cancelEditing = () => {
  isEditing.value = false

  // Reset form to original values
  if (props.parcel) {
    editedParcel.value = {
      ...props.parcel,
      transport: Number(props.parcel.transport),
      belongs_to: Number(props.parcel.belongs_to),
      customer_id: props.parcel.customer?.id,
      customer: props.parcel.customer,
      weight: Number(props.parcel.weight),
      length: Number(props.parcel.length),
      width: Number(props.parcel.width),
      height: Number(props.parcel.height),
      parcel_type: props.parcel.parcel_type || 'standard',
      notes: props.parcel.notes || '',
    }
  }
}

const saveChanges = async () => {
  try {
    loading.value = true

    // Check if customer is selected
    if (!editedParcel.value.customer_id && !editedParcel.value.customer?.id) {
      alert(t('shipment.customerRequired'))
      loading.value = false
      
      return
    }

    const payload = {
      ...editedParcel.value,
      verified: true, // Always set to true when saving
      status: 'received', // Change status to received
      status_key: 'received',
      customer_id: editedParcel.value.customer?.id || editedParcel.value.customer_id,

      // Include product data from the original parcel
      shipment_product_lines: props.parcel.shipment_product_lines || [],
      products: props.parcel.products || [],
      
      // Include other required fields
      transport: Number(editedParcel.value.transport),
      belongs_to: Number(editedParcel.value.belongs_to),
      parcel_type: editedParcel.value.parcel_type || 'standard',
      notes: editedParcel.value.notes || '',
    }

    const response = await axios.put(`/shipment/incoming-parcels/${editedParcel.value.id}`, payload)

    emit('update:parcel', response.data)
    isEditing.value = false
    loading.value = false
    dialog.value = false

    // Show success message
    toast.success(t('messages.parcelVerified', { barcode: editedParcel.value.barcode }))

    // Emit parcel update events for the parent component and global listeners
    emit('parcel-updated', response.data)
    
    window.dispatchEvent(new CustomEvent('parcel:updated', {
      detail: {
        parcel: response.data,
        action: 'updated',
      },
    }))
  } catch (error) {
    console.error('Error saving parcel:', error)
    toast.error(t('messages.errorSavingParcel'))
    loading.value = false
  }
}

// CustomerSelector handles search automatically, so we don't need manual search functions

const handleCustomerSelect = customer => {
  if (customer) {
    // CustomerSelector provides properly formatted customer objects
    customerSelection.value = customer
    
    editedParcel.value.customer = {
      id: customer.id,
      display: customer.text || customer.name,
      name: customer.name,
      phone: customer.phone,
    }
    editedParcel.value.customer_id = customer.id
  } else {
    // If customer was cleared, clear our data
    customerSelection.value = null
    editedParcel.value.customer = null
    editedParcel.value.customer_id = null
  }
}

// Customer creation handlers
const handleCustomerCreateNew = customer => {
  newCustomerName.value = customer.name
  showContactModal.value = true
}

const handleCustomerCreated = formattedCustomer => {
  showContactModal.value = false
  
  // Update editedParcel with the new customer
  editedParcel.value.customer = formattedCustomer
  editedParcel.value.customer_id = formattedCustomer.id
  
  // Set as selected in CustomerSelector
  customerSelection.value = formattedCustomer
}

// Handle edit button click
const editParcel = () => {
  if (props.parcel.status_key === 'pending_verification') {
    // If the parcel is pending verification, use inline editing
    startEditing()
  } else {
    // Otherwise, navigate to the full edit page
    router.push(`/shipment/incoming-parcels/${props.parcel.id}/edit`)
  }
}

// Handle delete button click
const deleteParcel = async () => {
  if (confirm(t('messages.confirmDelete'))) {
    try {
      loading.value = true
      await axios.delete(`/shipment/incoming-parcels/${props.parcel.id}`)
      dialog.value = false
      loading.value = false
      window.location.reload()
    } catch (error) {
      console.error('Error deleting parcel:', error)
      loading.value = false
    }
  }
}
</script>

<template>
  <VDialog
    v-model="dialog"
    max-width="1000"
    class="parcel-modal"
  >
    <VCard>
      <VCardTitle class="d-flex justify-space-between align-center pa-4">
        <span>{{ t('shipment.parcelDetails') }}</span>
        <div class="d-flex align-center gap-2">
          <VBtn 
            v-if="parcel?.status_key === 'pending_verification' && !isEditing" 
            color="success" 
            :loading="loading"
            prepend-icon="tabler-check" 
            size="small"
            @click="startEditing"
          >
            {{ t('actions.verify') }}
          </VBtn>
          <VBtn 
            v-if="isEditing"
            color="success" 
            :loading="loading"
            prepend-icon="tabler-check" 
            size="small"
            @click="saveChanges"
          >
            {{ t('actions.save') }}
          </VBtn>
          <VBtn 
            v-if="isEditing"
            color="secondary" 
            prepend-icon="tabler-x" 
            size="small"
            @click="cancelEditing"
          >
            {{ t('actions.cancel') }}
          </VBtn>
          <VBtn 
            v-if="!isEditing && canEdit"
            color="warning" 
            prepend-icon="tabler-edit" 
            size="small"
            @click="startEditing"
          >
            {{ t('actions.edit') }}
          </VBtn>
          <VBtn 
            v-if="!isEditing && canDelete"
            color="error" 
            prepend-icon="tabler-trash" 
            size="small"
            @click="deleteParcel"
          >
            {{ t('actions.delete') }}
          </VBtn>
          <VBtn
            v-if="!isEditing"
            icon
            variant="text"
            @click="dialog = false"
          >
            <VIcon>tabler-x</VIcon>
          </VBtn>
        </div>
      </VCardTitle>

      <VDivider />

      <VCardText class="pa-4">
        <VTabs v-model="activeTab">
          <VTab>{{ t('shipment.basicInfo') }}</VTab>
          <VTab>{{ t('shipment.products') }}</VTab>
          <VTab v-if="parcel?.images?.length">
            {{ t('shipment.images') }}
          </VTab>
        </VTabs>

        <VWindow
          v-model="activeTab"
          class="mt-4"
        >
          <!-- Basic Info Tab -->
          <VWindowItem value="0">
            <VRow>
              <VCol
                cols="12"
                md="6"
              >
                <VCard variant="outlined">
                  <VCardTitle class="text-h6 pa-4">
                    {{ t('shipment.parcelInfo') }}
                  </VCardTitle>
                  <VCardText>
                    <VList>
                      <VListItem>
                        <template #prepend>
                          <VIcon color="primary">
                            tabler-barcode
                          </VIcon>
                        </template>
                        <VListItemTitle>{{ t('shipment.barcode') }}</VListItemTitle>
                        <VListItemSubtitle>{{ parcel?.barcode || '-' }}</VListItemSubtitle>
                      </VListItem>

                      <VListItem>
                        <template #prepend>
                          <VIcon color="primary">
                            tabler-shopping-bag
                          </VIcon>
                        </template>
                        <VListItemTitle>{{ t('shipment.bag_code') }}</VListItemTitle>
                        <VListItemSubtitle>{{ parcel?.bag_code || '-' }}</VListItemSubtitle>
                      </VListItem>

                      <VListItem>
                        <template #prepend>
                          <VIcon color="primary">
                            tabler-calendar
                          </VIcon>
                        </template>
                        <VListItemTitle>{{ t('shipment.date') }}</VListItemTitle>
                        <VListItemSubtitle>{{ parcel.date }}</VListItemSubtitle>
                      </VListItem>

                      <VListItem>
                        <template #prepend>
                          <VIcon color="primary">
                            tabler-tag
                          </VIcon>
                        </template>
                        <VListItemTitle>{{ t('shipment.status') }}</VListItemTitle>
                        <VListItemSubtitle>
                          <VChip
                            :color="getStatusColor(parcel.status_key)"
                            size="small"
                          >
                            {{ t(`shipment.statuses.${parcel?.status_key ?? parcel?.status ?? 'pending_verification'}`) }}
                          </VChip>
                        </VListItemSubtitle>
                      </VListItem>

                      <VListItem>
                        <template #prepend>
                          <VIcon color="primary">
                            tabler-cube
                          </VIcon>
                        </template>
                        <VListItemTitle>{{ t('shipment.dimensions') }}</VListItemTitle>
                        <VListItemSubtitle>{{ dimension }}</VListItemSubtitle>
                      </VListItem>

                      <VListItem>
                        <template #prepend>
                          <VIcon color="primary">
                            tabler-box
                          </VIcon>
                        </template>
                        <VListItemTitle>{{ t('shipment.volume') }}</VListItemTitle>
                        <VListItemSubtitle>{{ volume }}</VListItemSubtitle>
                      </VListItem>

                      <VListItem>
                        <template #prepend>
                          <VIcon color="primary">
                            tabler-weight
                          </VIcon>
                        </template>
                        <VListItemTitle>{{ t('shipment.weight') }}</VListItemTitle>
                        <VListItemSubtitle>{{ weight }}</VListItemSubtitle>
                      </VListItem>
                      
                      <!-- Notes Display -->
                      <VListItem v-if="parcel?.notes">
                        <template #prepend>
                          <VIcon color="primary">
                            tabler-notes
                          </VIcon>
                        </template>
                        <VListItemTitle>{{ t('shipment.notes') }}</VListItemTitle>
                        <VListItemSubtitle class="notes-text">{{ parcel.notes }}</VListItemSubtitle>
                      </VListItem>
                      
                      <!-- Parcel Type Display -->
                      <VListItem v-if="parcel?.parcel_type">
                        <template #prepend>
                          <VIcon color="primary">
                            tabler-tag
                          </VIcon>
                        </template>
                        <VListItemTitle>{{ t('shipment.parcel_type') }}</VListItemTitle>
                        <VListItemSubtitle>
                          <VChip
                            :color="getParcelTypeColor(parcel.parcel_type)"
                            size="small"
                            variant="tonal"
                          >
                            {{ t(`shipment.parcel_types.${parcel.parcel_type}`) }}
                          </VChip>
                        </VListItemSubtitle>
                      </VListItem>
                      
                      <VListItem>
                        <template #prepend>
                          <VIcon color="primary">
                            tabler-package
                          </VIcon>
                        </template>
                        <VListItemTitle>{{ t('shipment.products') }}</VListItemTitle>
                        <VListItemSubtitle class="products-summary">
                          {{ translatedProducts || parcel.products_display_text || '-' }}
                        </VListItemSubtitle>
                      </VListItem>
                    </VList>
                  </VCardText>
                </VCard>
              </VCol>

              <VCol
                cols="12"
                md="6"
              >
                <VCard variant="outlined">
                  <VCardTitle class="text-h6 pa-4">
                    {{ t('shipment.shippingInfo') }}
                  </VCardTitle>
                  <VCardText>
                    <VList>
                      <!-- Customer Selection/Display -->
                      <VListItem v-if="!isEditing">
                        <template #prepend>
                          <VIcon color="primary">
                            tabler-user
                          </VIcon>
                        </template>
                        <VListItemTitle>{{ t('shipment.customer') }}</VListItemTitle>
                        <VListItemSubtitle>{{ parcel?.customer?.display || '-' }}</VListItemSubtitle>
                      </VListItem>
                      
                      <!-- Customer Selection Field when editing -->
                      <VListItem v-if="isEditing">
                        <div class="w-100 py-2">
                          <CustomerSelector
                            v-model="customerSelection"
                            @create-new="handleCustomerCreateNew"
                            @update:model-value="handleCustomerSelect"
                          />
                        </div>
                      </VListItem>

                      <!-- Mobile display -->
                      <VListItem v-if="!isEditing">
                        <template #prepend>
                          <VIcon color="primary">
                            tabler-phone
                          </VIcon>
                        </template>
                        <VListItemTitle>{{ t('shipment.mobile') }}</VListItemTitle>
                        <VListItemSubtitle>{{ parcel?.customer?.phone || '-' }}</VListItemSubtitle>
                      </VListItem>
                      
                      <!-- Mobile in edit mode -->
                      <VListItem v-if="isEditing && editedParcel.customer">
                        <template #prepend>
                          <VIcon color="primary">
                            tabler-phone
                          </VIcon>
                        </template>
                        <VListItemTitle>{{ t('shipment.mobile') }}</VListItemTitle>
                        <VListItemSubtitle>{{ editedParcel.customer.phone || '-' }}</VListItemSubtitle>
                      </VListItem>

                      <!-- Transport Display -->
                      <VListItem v-if="!isEditing">
                        <template #prepend>
                          <VIcon color="primary">
                            tabler-plane
                          </VIcon>
                        </template>
                        <VListItemTitle>{{ t('shipment.transport') }}</VListItemTitle>
                        <VListItemSubtitle>{{ transportLabel }}</VListItemSubtitle>
                      </VListItem>
                      
                      <!-- Transport Edit -->
                      <VListItem v-if="isEditing">
                        <div class="w-100 py-2">
                          <VLabel class="mb-1">
                            {{ t('shipment.transport') }}
                          </VLabel>
                          <VSelect
                            v-model="editedParcel.transport"
                            :items="transportOptions"
                            variant="outlined"
                            density="compact"
                            item-title="title"
                            item-value="value"
                          />
                        </div>
                      </VListItem>

                      <!-- Belongs To Display -->
                      <VListItem v-if="!isEditing">
                        <template #prepend>
                          <VIcon color="primary">
                            tabler-building
                          </VIcon>
                        </template>
                        <VListItemTitle>{{ t('shipment.belongs_to') }}</VListItemTitle>
                        <VListItemSubtitle>
                          <VChip
                            :color="getBelongsToColor"
                            size="small"
                          >
                            {{ belongsToLabel }}
                          </VChip>
                        </VListItemSubtitle>
                      </VListItem>
                      
                      <!-- Belongs To Edit - Only show for admins -->
                      <VListItem v-if="isEditing && isAdmin">
                        <div class="w-100 py-2">
                          <VLabel class="mb-1">
                            {{ t('shipment.belongs_to') }}
                          </VLabel>
                          <VSelect
                            v-model="editedParcel.belongs_to"
                            :items="belongsToOptions"
                            variant="outlined"
                            density="compact"
                            item-title="title"
                            item-value="value"
                          />
                        </div>
                      </VListItem>

                      <!-- Parcel Type Edit -->
                      <VListItem v-if="isEditing">
                        <div class="w-100 py-2">
                          <VLabel class="mb-1">
                            {{ t('shipment.parcel_type') }}
                          </VLabel>
                          <VSelect
                            v-model="editedParcel.parcel_type"
                            :items="parcelTypeOptions"
                            variant="outlined"
                            density="compact"
                            item-title="title"
                            item-value="value"
                          />
                        </div>
                      </VListItem>

                      <!-- Notes Edit -->
                      <VListItem v-if="isEditing">
                        <div class="w-100 py-2">
                          <VLabel class="mb-1">
                            {{ t('shipment.notes') }}
                          </VLabel>
                          <VTextarea
                            v-model="editedParcel.notes"
                            variant="outlined"
                            density="compact"
                            rows="3"
                            :placeholder="t('shipment.notesPlaceholder', 'Enter any additional notes...')"
                          />
                        </div>
                      </VListItem>

                      <VListItem v-if="parcel.commission_agent">
                        <template #prepend>
                          <VIcon color="primary">
                            tabler-user-check
                          </VIcon>
                        </template>
                        <VListItemTitle>{{ t('shipment.agent') }}</VListItemTitle>
                        <VListItemSubtitle>{{ parcel.commission_agent.name }}</VListItemSubtitle>
                      </VListItem>
                    </VList>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </VWindowItem>

          <!-- Products Tab -->
          <VWindowItem value="1">
            <!-- Main product summary -->
            <VAlert
              v-if="translatedProducts || parcel.products_display_text"
              color="info"
              variant="tonal"
              class="mb-4"
            >
              <strong>{{ t('shipment.productSummary') }}:</strong> {{ translatedProducts || parcel.products_display_text }}
            </VAlert>
            
            <VTable v-if="parcel?.shipment_product_lines?.length || parcel?.products?.length || parcel?.products_display_details?.length">
              <thead>
                <tr>
                  <th>{{ t('tableHeaders.product') }}</th>
                  <th class="text-center">
                    {{ t('tableHeaders.quantity') }}
                  </th>
                  <th>{{ t('tableHeaders.detail') }}</th>
                </tr>
              </thead>
              <tbody>
                <!-- Prioritize products_display_details (guaranteed to be translated) -->
                <template v-if="parcel.products_display_details && parcel.products_display_details.length">
                  <tr
                    v-for="(product, idx) in parcel.products_display_details"
                    :key="'detail-' + idx"
                  >
                    <td>{{ getLocalizedProductName(product) }}</td>
                    <td class="text-center">
                      {{ product.quantity }}
                    </td>
                    <td>{{ product.detail || '-' }}</td>
                  </tr>
                </template>
                
                <!-- Fall back to products from API if display_details are not available -->
                <template v-else-if="parcel.products && parcel.products.length">
                  <tr
                    v-for="product in parcel.products"
                    :key="product.id"
                  >
                    <td>{{ getLocalizedProductName(product) }}</td>
                    <td class="text-center">
                      {{ product.quantity }}
                    </td>
                    <td>{{ product.detail || '-' }}</td>
                  </tr>
                </template>
                
                <!-- Finally, use shipment_product_lines as last resort -->
                <template v-else>
                  <tr
                    v-for="product in parcel.shipment_product_lines"
                    :key="product.id"
                  >
                    <td>{{ getLocalizedProductName(product.shipment_product) }}</td>
                    <td class="text-center">
                      {{ product.quantity }}
                    </td>
                    <td>{{ product.detail || '-' }}</td>
                  </tr>
                </template>
              </tbody>
            </VTable>
            <div
              v-else
              class="text-center pa-4"
            >
              {{ t('messages.noProductsFound') }}
            </div>
          </VWindowItem>

          <!-- Images Tab -->
          <VWindowItem value="2">
            <VRow v-if="parcel?.images?.length">
              <VCol
                v-for="(image, index) in parcel?.images || []"
                :key="image.id"
                cols="12"
                sm="6"
                md="4"
              >
                <VImg
                  :src="image.url || image.image_path"
                  aspect-ratio="1"
                  cover
                  class="rounded cursor-pointer elevation-2"
                  @click="openImageViewer(index)"
                />
              </VCol>
            </VRow>
            <div
              v-else
              class="d-flex flex-column align-center py-8"
            >
              <img
                src="/img/default.png"
                width="120"
                height="120"
                class="mb-4"
                alt="No Image"
              >
              <p class="text-medium-emphasis">
                {{ t('shipment.noImagesAvailable') }}
              </p>
              
              <!-- Only show if we're on a parcel that can have images added -->
              <VBtn
                v-if="parcel && parcel.id"
                color="primary"
                prepend-icon="tabler-upload"
                class="mt-4"
                size="small"
                @click="$emit('request-upload-images')"
              >
                {{ t('actions.uploadImages') }}
              </VBtn>
            </div>
          </VWindowItem>
        </VWindow>
      </VCardText>
    </VCard>

    <!-- Image Viewer Dialog -->
    <VDialog
      v-model="showImageViewer"
      fullscreen
      :scrim="true"
      transition="dialog-bottom-transition"
    >
      <VCard>
        <VToolbar color="primary">
          <VBtn
            icon
            @click="showImageViewer = false"
          >
            <VIcon>tabler-x</VIcon>
          </VBtn>
          <VToolbarTitle>{{ t('shipment.imageViewer') }}</VToolbarTitle>
          <VSpacer />
          <VBtn
            v-if="parcel.images && parcel.images.length > 0"
            icon
            :disabled="activeImageIndex === 0"
            @click="activeImageIndex--"
          >
            <VIcon>tabler-chevron-left</VIcon>
          </VBtn>
          <VBtn
            v-if="parcel.images && parcel.images.length > 0"
            icon
            :disabled="activeImageIndex === (parcel.images?.length - 1)"
            @click="activeImageIndex++"
          >
            <VIcon>tabler-chevron-right</VIcon>
          </VBtn>
        </VToolbar>
        <VCardText
          class="d-flex align-center justify-center"
          style="height: calc(100vh - 64px)"
        >
          <VImg
            v-if="parcel.images && parcel.images.length > 0"
            :src="parcel.images?.[activeImageIndex]?.url || parcel.images?.[activeImageIndex]?.image_path"
            max-height="90vh"
            contain
          />
          <div
            v-else
            class="d-flex flex-column align-center"
          >
            <img
              src="/img/default.png"
              width="200"
              height="200"
              class="mb-4"
              alt="No Image"
            >
            <p class="text-medium-emphasis">
              {{ t('shipment.noImagesAvailable') }}
            </p>
          </div>
        </VCardText>
      </VCard>
    </VDialog>
    
    <!-- Customer Creation Modal -->
    <ContactForm
      v-model="showContactModal"
      :initial-name="newCustomerName"
      @created="handleCustomerCreated"
    />
  </VDialog>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
}

.cursor-pointer:hover {
  transform: scale(1.02);
}

.dialog-bottom-transition-enter-active,
.dialog-bottom-transition-leave-active {
  transition: transform 0.2s ease-in-out;
}

.dialog-bottom-transition-enter-from,
.dialog-bottom-transition-leave-to {
  transform: translateY(100%);
}

.products-summary {
  max-width: 300px;
  white-space: normal;
  word-wrap: break-word;
}

.notes-text {
  max-width: 350px;
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.4;
}
</style>
